import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'antd-mobile';
import { checkWsQuickRegister } from '../services/api';
import { clearWsToken, isWsTokenError } from '../utils/wsToken';

const CHECK_ATTEMPTS = 5;
const CHECK_DELAY_MS = 1500;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 可选前置校验：仅当 token 明确绑定无效 phone（WS_PHONE_REQUIRED）时跳转 OTP。
 * token 尚未写入库（YC 竞态）时重试；校验接口不可用时不阻断 WS 注册（与改前行为一致）。
 */
export function useWsQuickRegisterGate(wsToken) {
  const navigate = useNavigate();
  const [gateReady, setGateReady] = useState(!wsToken);
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (!wsToken) {
      setGateReady(true);
      return undefined;
    }

    let cancelled = false;
    setGateReady(false);
    redirectedRef.current = false;

    (async () => {
      for (let attempt = 1; attempt <= CHECK_ATTEMPTS; attempt += 1) {
        if (cancelled) {
          return;
        }
        try {
          const ctx = await checkWsQuickRegister(wsToken);
          if (cancelled) {
            return;
          }
          if (ctx?.quickRegister) {
            setGateReady(true);
            return;
          }
          if (ctx?.reason === 'WS_PHONE_REQUIRED') {
            if (redirectedRef.current) {
              return;
            }
            redirectedRef.current = true;
            clearWsToken();
            const q = new URLSearchParams();
            if (ctx?.shopId != null && ctx?.shopId !== '') {
              q.set('id', String(ctx.shopId));
            }
            Toast.show({
              content: 'Please register with your mobile number and verification code.',
            });
            navigate({ pathname: '/register', search: q.toString() }, { replace: true });
            return;
          }
          if (ctx?.reason === 'INVALID_OR_EXPIRED_TOKEN' && attempt < CHECK_ATTEMPTS) {
            await sleep(CHECK_DELAY_MS);
            continue;
          }
          setGateReady(true);
          return;
        } catch (e) {
          const msg = e?.message || '';
          if (isWsTokenError(msg) && attempt < CHECK_ATTEMPTS) {
            await sleep(CHECK_DELAY_MS);
            continue;
          }
          if (!cancelled) {
            setGateReady(true);
          }
          return;
        }
      }
      if (!cancelled) {
        setGateReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [wsToken, navigate]);

  return { gateReady };
}
